/**
 * UserAnnotation Types
 *
 * Shared types for user-authored annotations on a block. A `UserAnnotation`
 * is one of five kinds of mark laid on top of a block:
 *
 *   - `drawing`         freehand strokes with a transform (width/height/rotation)
 *   - `text_highlight`  a colored span anchored to a text range in the block
 *   - `sticker`         a sticker placed at a position with a transform
 *   - `stamp`           a rubber-stamp text mark (preset like "TODO" or any
 *                       custom string) at a position with a height-only
 *                       transform; width is intrinsic to the rendered text
 *   - `comment`         text content, optionally attached to another annotation
 *                       via `parentId` (comment-on-mark or thread reply) and/or
 *                       a polymorphic `anchor` selector pointing at a part of
 *                       the block
 *
 * Permission for every UserAnnotation flows through the parent block — the
 * `ownerId` field is purely identity/audit. Read/write access is checked
 * against `blockId` via `BlockEntity.hasReadAccess` / `hasWriteAccess`.
 *
 * This entity is **separate** from the existing internal `Annotation` table
 * (which is used for ingestor errors, assistant comments, etc.). They share
 * no schema and no migration history.
 *
 * All types are defined as Zod schemas with inferred TypeScript types.
 */
import { z } from "zod";
import { prefixedIdSchema, } from "./id";
// ---------------------------------------------------------------------------
// Limits
// ---------------------------------------------------------------------------
/** Max length of a comment body string. */
export const MAX_USER_ANNOTATION_COMMENT_BODY_LENGTH = 50_000;
/** Max number of strokes in a single drawing annotation. */
export const MAX_USER_ANNOTATION_DRAWING_STROKES = 1_000;
/** Max number of points in a single drawing stroke. */
export const MAX_USER_ANNOTATION_STROKE_POINTS = 10_000;
/** Max length of the `quote` snapshot in a text_range selector. */
export const MAX_USER_ANNOTATION_QUOTE_LENGTH = 5_000;
/** Max length of the `stickerId` field. */
export const MAX_USER_ANNOTATION_STICKER_ID_LENGTH = 200;
/** Max length of a CSS color string in `content`. */
export const MAX_USER_ANNOTATION_COLOR_LENGTH = 50;
/** Max length of the `text` field on a stamp annotation. */
export const MAX_USER_ANNOTATION_STAMP_TEXT_LENGTH = 200;
/**
 * Cap on `blockIds.length` for the batched list endpoint
 * (`POST /v1/user-annotation/by-blocks`). Stream pages are bounded
 * well below this; the cap is defense-in-depth so a crafted client
 * can't fan out to scan every block in one request. Lives in shared
 * types so the client and server agree on the maximum without each
 * inlining its own constant.
 */
export const MAX_USER_ANNOTATION_BATCH_BLOCK_IDS = 200;
// ---------------------------------------------------------------------------
// Annotation Type
// ---------------------------------------------------------------------------
export const UserAnnotationTypeSchema = z.enum([
    "drawing",
    "text_highlight",
    "sticker",
    "stamp",
    "comment",
]);
// ---------------------------------------------------------------------------
// Transform — shared by drawing + sticker
//
// Width and height are normalized 0–1 of the panel. Rotation is in degrees,
// clockwise from horizontal, pivoting around the annotation's center.
//
// `naturalWidth` / `naturalHeight` (drawing-only, optional) capture the
// bbox dimensions at commit time. They're frozen — never updated by a
// resize gesture — so the renderer can drive its SVG viewBox from the
// drawing's natural pixel size and let the host element scale strokes
// photographically. That gives Figma-style "outlined stroke" behavior
// where stroke weight scales with the drawing's current size relative
// to its natural size. Stickers don't carry these (stickers have a
// fixed intrinsic visual; their `width`/`height` already serve the
// uniform scale). Legacy drawings (rows stored before the field was
// introduced) leave them undefined; renderers fall back to treating
// the current `width`/`height` as natural.
// ---------------------------------------------------------------------------
export const TransformSchema = z.object({
    width: z.number().min(0).max(2),
    height: z.number().min(0).max(2),
    rotation: z.number(),
    naturalWidth: z.number().min(0).max(2).optional(),
    naturalHeight: z.number().min(0).max(2).optional(),
});
// ---------------------------------------------------------------------------
// StampTransform — height + rotation only.
//
// Stamps render text whose intrinsic width is determined by the rendered
// glyphs themselves (`width: max-content`); the renderer scales the
// whole stamp uniformly via `transform: scale(height / BASE_HEIGHT)` so
// font-size, padding, and border thickness all grow together. There's
// no independent width axis to persist — sliding the corner-resize
// handle only changes `height`.
//
// Position (`x`, `y`) is no longer part of the transform — every type
// now reads its on-host position from the top-level `anchor` field
// (`anchor.type === "panel_point"` for stamps). The migration that
// landed Phase 2 backfills existing rows so this schema can drop the
// legacy `(x, y)` axes without losing any user data.
// ---------------------------------------------------------------------------
export const StampTransformSchema = z.object({
    height: z.number().min(0).max(2),
    rotation: z.number(),
});
// ---------------------------------------------------------------------------
// Coordinate-system contract — anchor vs transform
//
// `anchor` answers "what this annotation is about and when/where it
// surfaces" — the address in the host content's native coordinate
// space. v1 selector types:
//   whole        → the entire block (general comments / commentary)
//   region       → a rectangular area of the panel (labeled area)
//   text_range   → a span of characters in the rendered text
//   panel_point  → a single normalized point on the rendered panel
//                   (used by stamps, future stickers, freehand pin-marks)
//
// Future, deferred until the matching block viewers ship:
//   time_point   → a moment in an audio/video timeline
//   time_range   → a span of time in an audio/video timeline
//   page         → a page in a multi-page document
//   path         → a structured path in a JSON-shaped block (LinkedIn etc.)
//   compound     → AND of the above, e.g. (time_range × panel_point) for a
//                  video-frame sticker visible during 12s–17s at (0.85,0.75)
//
// `transform` (carried inside content for marks that have one — drawing,
// sticker, stamp) answers "how large and how rotated the mark is when
// drawn". Position-on-panel is *not* a transform property; it's derived
// from the anchor's spatial selector. Marks without anchors fall
// through to renderer defaults (e.g. drag-and-drop sticker without an
// anchor uses the rendered drag position).
//
// Renderers resolve in two passes:
//   1. Resolve `anchor` → an attach point (and optional bounds) in the
//      panel's render coordinates. Temporal selectors GATE visibility
//      (annotation hidden when outside the time window); spatial
//      selectors POSITION the mark.
//   2. Draw the mark sized by `transform.width × transform.height` and
//      rotated by `transform.rotation`, centered on the resolved attach
//      point. Marks without a transform (text_highlight, comment) use
//      anchor-implied bounds.
//
// `compound` is the operator that lets a single anchor combine "where in
// space" with "when in time" without exploding the kind enum into per-
// host combinations (no `video_point`, `audio_caption_position`, etc.).
// Invariants: at most one spatial selector, at most one temporal
// selector, no nested compounds.
//
// Every annotation type may carry an `anchor` — Phase 2 of the
// annotations refactor opened the field up to drawings, text_highlights,
// stickers, and stamps. (Previously only comments carried `anchor`,
// with stamps storing `(x, y)` inside `transform` and text_highlights
// storing `range` inside `content`.) The migration that introduced
// the broader `anchor` rule backfills both legacy paths.
// ---------------------------------------------------------------------------
export const WholeSelectorSchema = z.object({
    type: z.literal("whole"),
});
export const RegionSelectorSchema = z.object({
    type: z.literal("region"),
    /**
     * The area of the panel being annotated, normalized 0–1. This is the
     * "what" — the area being labeled — not a position-store. Renderers
     * derive a draw-position from the centroid when needed.
     */
    bbox: z.object({
        x: z.number(),
        y: z.number(),
        w: z.number(),
        h: z.number(),
    }),
});
export const TextRangeSelectorSchema = z.object({
    type: z.literal("text_range"),
    /** Snapshot of the matched text — used as a fuzzy fallback when offsets drift. */
    quote: z.string().max(MAX_USER_ANNOTATION_QUOTE_LENGTH),
    /** Inclusive character start offset into the block's rendered text. */
    start: z.number().int().nonnegative(),
    /** Exclusive character end offset into the block's rendered text. */
    end: z.number().int().nonnegative(),
});
/**
 * `panel_point` — a single normalized 0..1 point on the host panel.
 * Used by stamps (their dropped position), and (when sticker placement
 * lands as a real persistence path) stickers and any future freehand
 * pin-marks. A small overshoot on each axis (`-0.5`..`1.5`) lets the
 * resize/spin gestures push the mark past the host's edges briefly
 * without the value clamping.
 */
export const PanelPointSelectorSchema = z.object({
    type: z.literal("panel_point"),
    x: z.number().min(-0.5).max(1.5),
    y: z.number().min(-0.5).max(1.5),
});
export const SelectorSchema = z.discriminatedUnion("type", [
    WholeSelectorSchema,
    RegionSelectorSchema,
    TextRangeSelectorSchema,
    PanelPointSelectorSchema,
]);
// ---------------------------------------------------------------------------
// Stroke — used by drawing
//
// Stroke points are normalized 0–1 within the drawing's own `transform`
// bounding box (not the panel). This lets the drawing be moved / resized /
// rotated by editing only `transform` without rewriting every stroke point.
// ---------------------------------------------------------------------------
export const StrokePointSchema = z.object({
    x: z.number(),
    y: z.number(),
    pressure: z.number().min(0).max(1).optional(),
});
export const StrokeSchema = z.object({
    points: z.array(StrokePointSchema).min(1).max(MAX_USER_ANNOTATION_STROKE_POINTS),
});
// ---------------------------------------------------------------------------
// Per-`type` Content Schemas
// ---------------------------------------------------------------------------
export const DrawingContentSchema = z.object({
    strokes: z.array(StrokeSchema).min(1).max(MAX_USER_ANNOTATION_DRAWING_STROKES),
    color: z.string().max(MAX_USER_ANNOTATION_COLOR_LENGTH),
    thickness: z.number().positive(),
    opacity: z.number().min(0).max(1).optional(),
    transform: TransformSchema,
});
/**
 * TextHighlightContent — color only. The `range` selector that
 * previously lived here under `content.range` is now persisted at the
 * row's top-level `anchor` field as a `text_range` selector. The
 * Phase 2 migration backfills existing rows; renderers read the range
 * via `annotation.anchor`.
 */
export const TextHighlightContentSchema = z.object({
    color: z.string().max(MAX_USER_ANNOTATION_COLOR_LENGTH),
});
export const StickerContentSchema = z.object({
    stickerId: z.string().min(1).max(MAX_USER_ANNOTATION_STICKER_ID_LENGTH),
    transform: TransformSchema,
});
/**
 * Visual variants for a stamp. Drives renderer chrome only — the
 * placement / gesture / persistence path is identical for both:
 *
 *   - `label` (default for legacy rows) — classic rubber-stamp look:
 *     uppercase text wrapped in a colored border, slight tilt, bold
 *     letterforms. Used by the WIP/TODO/DONE/VOID/BOOOO presets and
 *     any custom-text stamp from the rubber-stamp popover.
 *   - `emoji` — bare-glyph rendering for emoji reactions: no border,
 *     no uppercase, no tilt, larger intrinsic font-size so the
 *     emoji reads as a "reaction" rather than a labeled mark.
 *
 * Optional so existing rows (created before the field landed)
 * default to `label` without a migration. New emoji-reaction rows
 * persist `style: "emoji"`.
 */
export const StampStyleSchema = z.enum(["label", "emoji"]);
/**
 * StampContent — text (preset like "TODO", any custom string, or an
 * emoji reaction), a CSS hex color (`#RGB` / `#RRGGBB` / `#RRGGBBAA`),
 * and a height-only transform. Width is intrinsic to the rendered
 * text; renderers apply a uniform scale of `height / BASE_HEIGHT` so
 * font-size, padding, and border thickness scale together.
 *
 * The optional `style` discriminator selects between the rubber-stamp
 * label visual (default) and the emoji-reaction visual — same
 * underlying transform / anchor / gesture model in either case.
 */
export const StampContentSchema = z.object({
    text: z.string().min(1).max(MAX_USER_ANNOTATION_STAMP_TEXT_LENGTH),
    color: z
        .string()
        .max(MAX_USER_ANNOTATION_COLOR_LENGTH)
        .regex(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/),
    transform: StampTransformSchema,
    style: StampStyleSchema.optional(),
});
/** Optional comment colour label — a lightweight green/red flag (e.g. ok / blocker). */
export const CommentColorSchema = z.enum(["green", "red"]);
export const CommentContentSchema = z.object({
    body: z.string().min(1).max(MAX_USER_ANNOTATION_COMMENT_BODY_LENGTH),
    /** Optional colour flag chosen in the composer; absent = no colour. */
    color: CommentColorSchema.optional(),
});
// ---------------------------------------------------------------------------
// UserAnnotation Base Schema
// ---------------------------------------------------------------------------
const UserAnnotationBaseSchema = z.object({
    id: prefixedIdSchema("userAnnotation"),
    blockId: prefixedIdSchema("block"),
    ownerId: z.string(),
    /**
     * Optional self-reference. Used for:
     *   - comment-on-mark (parent is a drawing/highlight/sticker)
     *   - thread reply (parent is another comment)
     *
     * The entity validates that the parent exists, lives on the same `blockId`,
     * and is not soft-deleted. Cycle prevention is unnecessary while only
     * comments use `parentId` and they only attach upward.
     */
    parentId: prefixedIdSchema("userAnnotation").optional(),
    /**
     * Polymorphic selector pointing at the part of the block this
     * annotation is attached to. Optional — when omitted (or when set
     * to `{ type: "whole" }`) the annotation applies to the whole
     * block. Specific defaults by type:
     *
     *   - `text_highlight` and `text_range` comments expect a
     *     `text_range` anchor; without one the renderer can't paint.
     *   - `stamp` and (future) `sticker` placements use `panel_point`
     *     for the dropped (x, y).
     *   - `comment` with `text_range` paints inline highlight rects
     *     over the matched span; `whole` / `region` / unanchored
     *     comments have no overlay surface and live entirely in the
     *     comments rail (and the touch-only rail-replacement popover).
     *   - `drawing` ignores anchor today (strokes self-position via
     *     their own normalized 0–1 coords); reserved for future
     *     gesture-driven repositioning.
     */
    anchor: SelectorSchema.optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime().optional(),
    deletedAt: z.string().datetime().optional(),
});
// ---------------------------------------------------------------------------
// UserAnnotation Schema (discriminated union)
//
// Every variant carries the optional top-level `anchor`. Phase 2 of
// the annotations refactor unified the position story across all
// types: text_highlights moved their `range` from `content.range` to
// `anchor`, and stamps moved their `(x, y)` from `content.transform`
// to `anchor`. Drawings and (future) stickers can opt in too, in
// which case the gesture handlers patch `anchor` rather than each
// type carrying its own position channel inside `content`.
// ---------------------------------------------------------------------------
export const UserAnnotationSchema = z.discriminatedUnion("type", [
    UserAnnotationBaseSchema.extend({
        type: z.literal("drawing"),
        content: DrawingContentSchema,
    }),
    UserAnnotationBaseSchema.extend({
        type: z.literal("text_highlight"),
        content: TextHighlightContentSchema,
    }),
    UserAnnotationBaseSchema.extend({
        type: z.literal("sticker"),
        content: StickerContentSchema,
    }),
    UserAnnotationBaseSchema.extend({
        type: z.literal("stamp"),
        content: StampContentSchema,
    }),
    UserAnnotationBaseSchema.extend({
        type: z.literal("comment"),
        content: CommentContentSchema,
    }),
]);
